import { useSyncExternalStore } from 'react'
import { ServerApiClient } from './ServerApiClient'

export type ApiSessionSnapshot = {
  token: string | null
  refreshToken: string | null
  initialized: boolean
  bootstrapping: boolean
  error: string | null
}

const AUTH_TOKEN_KEY = 'fusegrid_api_token'
const AUTH_REFRESH_TOKEN_KEY = 'fusegrid_api_refresh_token'
const listeners = new Set<() => void>()

let listenersAttached = false
let bootstrapPromise: Promise<ApiSessionSnapshot> | null = null
let bootstrapRetryTimer: number | null = null
let snapshot: ApiSessionSnapshot = {
  token: null,
  refreshToken: null,
  initialized: false,
  bootstrapping: false,
  error: null,
}

const decodeJwtExpiryUnixMs = (tokenRaw: string | null | undefined): number | null => {
  const token = String(tokenRaw || '').trim()
  if (!token) return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  const payload = parts[1]
  if (!payload) return null
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const decoded = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('utf8')
    const parsed = JSON.parse(decoded)
    const exp = Number(parsed?.exp)
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
  } catch {
    return null
  }
}

const readStorageSession = (): Pick<ApiSessionSnapshot, 'token' | 'refreshToken'> => {
  if (typeof window === 'undefined') {
    return { token: null, refreshToken: null }
  }
  try {
    return {
      token: String(window.localStorage.getItem(AUTH_TOKEN_KEY) || '').trim() || null,
      refreshToken: String(window.localStorage.getItem(AUTH_REFRESH_TOKEN_KEY) || '').trim() || null,
    }
  } catch {
    return { token: null, refreshToken: null }
  }
}

const emit = () => {
  for (const listener of listeners) listener()
}

const snapshotsEqual = (left: ApiSessionSnapshot, right: ApiSessionSnapshot): boolean =>
  left.token === right.token &&
  left.refreshToken === right.refreshToken &&
  left.initialized === right.initialized &&
  left.bootstrapping === right.bootstrapping &&
  left.error === right.error

const composeSnapshot = (partial?: Partial<ApiSessionSnapshot>): ApiSessionSnapshot => ({
  ...snapshot,
  ...readStorageSession(),
  ...(partial ?? {}),
})

const commitSnapshot = (next: ApiSessionSnapshot, options?: { emit?: boolean }): ApiSessionSnapshot => {
  if (snapshotsEqual(snapshot, next)) {
    return snapshot
  }
  snapshot = next
  if (options?.emit !== false) emit()
  return snapshot
}

const syncSnapshot = (partial?: Partial<ApiSessionSnapshot>, options?: { emit?: boolean }): ApiSessionSnapshot => {
  const next = composeSnapshot(partial)
  return commitSnapshot(next, options)
}

const attachWindowListeners = () => {
  if (listenersAttached || typeof window === 'undefined') return
  listenersAttached = true
  window.addEventListener('storage', () => {
    syncSnapshot()
  })
  window.addEventListener('fusegrid_api_auth_state_changed', () => {
    syncSnapshot()
  })
}

const hasFreshAccessToken = (token: string | null | undefined): boolean => {
  const expiryMs = decodeJwtExpiryUnixMs(token)
  if (expiryMs == null) return Boolean(String(token || '').trim())
  return expiryMs > Date.now() + 30_000
}

const isTransientApiSessionBootstrapError = (error: any): boolean => {
  const status = Number(error?.status ?? NaN)
  const message = String(error?.message || error?.body || error || '').toLowerCase()
  return (
    (Number.isFinite(status) && (status >= 500 || status === 429)) ||
    message.includes('pool timed out') ||
    message.includes('timeout') ||
    message.includes('temporarily unavailable') ||
    message.includes('failed to fetch') ||
    message.includes('networkerror')
  )
}

const clearBootstrapRetryTimer = () => {
  if (!bootstrapRetryTimer) return
  clearTimeout(bootstrapRetryTimer)
  bootstrapRetryTimer = null
}

const scheduleBootstrapRetry = () => {
  if (bootstrapRetryTimer || typeof window === 'undefined') return
  bootstrapRetryTimer = window.setTimeout(() => {
    bootstrapRetryTimer = null
    void ensureApiSessionReady()
  }, 5_000)
}

export const getApiSessionSnapshot = (): ApiSessionSnapshot => {
  attachWindowListeners()
  return syncSnapshot(undefined, { emit: false })
}

export const subscribeApiSession = (listener: () => void): (() => void) => {
  attachWindowListeners()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const useApiSessionSnapshot = (): ApiSessionSnapshot =>
  useSyncExternalStore(subscribeApiSession, getApiSessionSnapshot, getApiSessionSnapshot)

export const ensureApiSessionReady = async (): Promise<ApiSessionSnapshot> => {
  attachWindowListeners()
  const current = syncSnapshot(undefined, { emit: false })
  if (hasFreshAccessToken(current.token)) {
    return syncSnapshot({
      initialized: true,
      bootstrapping: false,
      error: null,
    })
  }
  if (bootstrapPromise) return await bootstrapPromise

  bootstrapPromise = (async () => {
    syncSnapshot({
      initialized: true,
      bootstrapping: true,
      error: null,
    })
    const api = new ServerApiClient()
    try {
      if (current.refreshToken) {
        const refreshed = await api.refreshAuthSession()
        if (refreshed?.access_token) {
          clearBootstrapRetryTimer()
          return syncSnapshot({
            initialized: true,
            bootstrapping: false,
            error: null,
          })
        }
      }
    } catch {
      // fall through to local bootstrap when available
    }

    if (api.localAuthBootstrapCandidate()) {
      try {
        const bootstrapped = await api.bootstrapLocalAuthSession()
        if (bootstrapped?.login?.access_token) {
          clearBootstrapRetryTimer()
          return syncSnapshot({
            initialized: true,
            bootstrapping: false,
            error: null,
          })
        }
      } catch (error: any) {
        if (isTransientApiSessionBootstrapError(error)) {
          scheduleBootstrapRetry()
          return syncSnapshot({
            initialized: true,
            bootstrapping: true,
            error: String(error?.message || error || ''),
          })
        }
        return syncSnapshot({
          initialized: true,
          bootstrapping: false,
          error: String(error?.message || error || ''),
        })
      }
    }

    return syncSnapshot({
      initialized: true,
      bootstrapping: false,
      error: null,
    })
  })()

  try {
    return await bootstrapPromise
  } finally {
    bootstrapPromise = null
  }
}

export const resetApiSessionForTests = (): void => {
  bootstrapPromise = null
  clearBootstrapRetryTimer()
  snapshot = {
    token: null,
    refreshToken: null,
    initialized: false,
    bootstrapping: false,
    error: null,
  }
}
